import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { TextInputV2 } from "react-migration/components/TextInputV2";
import { FiltersWithClear } from "react-migration/domains/comparables/components/Filters/FiltersWithClear";
import { ComparablesFilterOptions } from "react-migration/domains/comparables/typings/Comparables";

import { useSelectionSidebarLoading } from "react-migration/layouts/map/Multilayer/Bundle/useSelectionSidebarLoadingState";
import { logEvent } from "react-migration/lib/util/logEvent";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { SelectionType } from "src/js/stores/map/store";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { SearchIcon } from "../components/SearchIcon";
import { CollapsibleConsiderationsCard } from "../../Bundle/Workbench/AssessmentTab/CollapsibleConsiderationsCard";
import { useFilterStateTracking } from "../../hooks/useFilterStateTracking";
import { LayerTypeSelectionSidebarProps } from "../../types";
import { ComparablesSelectionSidebarHeader } from "./ComparablesSelectionSidebarHeader";
import { useTransactionsAtSelection } from "./useTransactionsAtSelection";
import { TransactionsAccordion } from "./TransactionsAccordion";
import { useComparablesFilterState } from "./state/useComparablesFilterState";
import { useRadiusInput } from "react-migration/lib/map/useRadiusInput";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import Feature from "src/js/stores/user/Feature";
import { CardTabs } from "../../Bundle/Workbench/AssessmentTab/CardTabs";
import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { MeasurementSystem } from "src/js/stores/user/store";
import { ScopeType } from "src/js/stores/site/store";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import { useSnapshot } from "valtio";
import { comparablesStore } from "src/js/stores/comparables";
import { COMPARABLES_LIMIT, COMPARABLES_ZOOPLA_LIMIT } from "react-migration/constants";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { useComparablesLayerContext } from "./ComparableLayerContext/useComparableLayerContext";
import { isEqual } from "lodash";
import { ComparablesDisplayByDropdown } from "./ComparablesDisplayByDropdown";

function ComparablesSelectionSidebarInner({
  layer,
  selection,
  setDetailSelection,
  cardExpanded,
  onLoadingChange,
}: LayerTypeSelectionSidebarProps) {
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const { priceMode, RHPdisplayBy } = useSnapshot(comparablesStore);

  // Use dataSource for FiltersWithClear component
  const [tab, setTab] = useState<ScopeType>(ScopeType.ON_SITE);
  const { RadiusInput, bufferedGeometry } = useRadiusInput({
    geometry: selection?.feature?.geometry || null,
    enabled: tab === ScopeType.NEARBY,
    defaultRadius: 100,
  });

  const { debouncedTrackControlChange } = useFilterStateTracking(layer.title);
  const unitPreference = getMeasurementSystemPreference();

  const comparablesLayerConfig = (layer?.layerConfig ?? {}) as {
    localStorageKey?: string;
    initialFilterState?: Partial<ComparablesFilterOptions>;
  };
  const [state, { hasNoFilters, updateFilters, clearFilters }] = useComparablesFilterState(
    comparablesLayerConfig.localStorageKey || "comparables-selection-sidebar",
    comparablesLayerConfig.initialFilterState,
    isZoopla(RHPdisplayBy) ? "hometrack" : "hmlr",
    debouncedTrackControlChange
  );

  const siteData = useTransactionsAtSelection(selection, setDetailSelection, state);

  const nearbySelection = useMemo(() => ({ ...selection, feature: bufferedGeometry }), [selection, bufferedGeometry]);
  const nearbyData = useTransactionsAtSelection(nearbySelection, setDetailSelection, state);

  const { setTransactions, setZooplaTransactions } = useComparablesLayerContext();

  const lastTransactionsRef = useRef<unknown>();
  const lastZooplaResultsRef = useRef<unknown>();

  useEffect(() => {
    const currentData = tab === ScopeType.NEARBY ? nearbyData : siteData;

    if (!isEqual(currentData.enrichedResults, lastTransactionsRef.current)) {
      setTransactions(currentData.enrichedResults);
      lastTransactionsRef.current = currentData.enrichedResults;
    }

    if (!isEqual(currentData.zooplaResults, lastZooplaResultsRef.current)) {
      setZooplaTransactions(currentData.zooplaResults);
      lastZooplaResultsRef.current = currentData.zooplaResults;
    }
  }, [tab, nearbyData, siteData, setTransactions, setZooplaTransactions]);

  const siteFilteredByAddress = useMemo(
    () =>
      siteData.includedResults.filter(
        (comp) =>
          comp.address.toLowerCase().includes(search.toLowerCase()) ||
          Object.values(comp.zooplaEnrichment?.location?.address ?? {}).some((value) =>
            value?.toLowerCase().includes(search.toLowerCase())
          )
      ),
    [siteData.includedResults, search]
  );

  const nearbyFilteredByAddress = useMemo(
    () => nearbyData.includedResults.filter((comp) => comp.address.toLowerCase().includes(search.toLowerCase())),
    [nearbyData.includedResults, search]
  );

  useEffect(() => {
    logEvent("Comparables Filters on point select", Object.assign({ ...state }));
  }, [state]);

  useSelectionSidebarLoading(onLoadingChange, siteData.loading);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), []);

  const handleShowFilter = useCallback(() => {
    logEventV2({
      name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
      properties: {
        cardName: layer.title,
        filterName: "Show",
        filterState: !showFilter,
      },
    });
    setShowFilter(!showFilter);
  }, [showFilter, layer.title]);

  return (
    <CollapsibleConsiderationsCard
      heading={<ComparablesSelectionSidebarHeader count={siteData.includedResults.length} />}
      name="Comparables"
      defaultExpanded={cardExpanded}
    >
      <CardTabs
        tab={tab}
        onTabChange={setTab}
        siteResults={{
          filtered: siteFilteredByAddress.length,
          all: siteData.includedResults.length,
          limit: isZoopla(priceMode) ? COMPARABLES_ZOOPLA_LIMIT : COMPARABLES_LIMIT,
          loading: siteData.loading,
        }}
        nearbyResults={{
          filtered: nearbyFilteredByAddress.length,
          all: nearbyData.includedResults.length,
          limit: isZoopla(priceMode) ? COMPARABLES_ZOOPLA_LIMIT : COMPARABLES_LIMIT,
          loading: nearbyData.loading,
        }}
      >
        <CardTabs.SiteTab
          error={!!siteData.error}
          loading={siteData.loading}
          resultsCount={siteData.includedResults.length}
          setTab={setTab}
        >
          <ComparablesCardContent
            search={search}
            showFilter={showFilter}
            hasNoFilters={hasNoFilters}
            clearFilters={clearFilters}
            filteredByAddress={siteFilteredByAddress}
            loading={siteData.loading}
            selectRecord={siteData.selectRecord}
            handleInputChange={handleInputChange}
            handleShowFilter={handleShowFilter}
            state={state}
            updateFilters={updateFilters}
            unitPreference={unitPreference}
          />
        </CardTabs.SiteTab>
        <CardTabs.NearbyTab
          error={!!nearbyData.error}
          loading={nearbyData.loading}
          resultsCount={nearbyData.includedResults.length}
          radiusInput={RadiusInput}
        >
          <ComparablesCardContent
            search={search}
            showFilter={showFilter}
            hasNoFilters={hasNoFilters}
            clearFilters={clearFilters}
            filteredByAddress={nearbyFilteredByAddress}
            loading={nearbyData.loading}
            selectRecord={nearbyData.selectRecord}
            handleInputChange={handleInputChange}
            handleShowFilter={handleShowFilter}
            state={state}
            updateFilters={updateFilters}
            unitPreference={unitPreference}
          />
        </CardTabs.NearbyTab>
      </CardTabs>
    </CollapsibleConsiderationsCard>
  );
}

type ComparablesCardContentProps = {
  search: string;
  showFilter: boolean;
  hasNoFilters: boolean;
  clearFilters: () => void;
  filteredByAddress: IComparablesTransactionDTO[];
  loading: boolean;
  selectRecord: (record: IComparablesTransactionDTO | undefined) => void;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleShowFilter: () => void;
  state: ComparablesFilterOptions;
  updateFilters: (val: Partial<ComparablesFilterOptions>) => void;
  unitPreference: MeasurementSystem;
};

const ComparablesCardContent = ({
  search,
  showFilter,
  hasNoFilters,
  clearFilters,
  filteredByAddress,
  loading,
  selectRecord,
  handleInputChange,
  handleShowFilter,
  state,
  updateFilters,
  unitPreference,
}: ComparablesCardContentProps) => {
  const { t } = useTranslation();
  const { RHPdisplayBy } = useSnapshot(comparablesStore);

  return (
    <>
      {(hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess)) &&
        !hasFeature(Feature.usAccess) && <ComparablesDisplayByDropdown />}

      <div className="flex items-center p-2 space-x-2">
        <div className="grow">
          <TextInputV2
            dataTestId="labels-search-input"
            value={search}
            placeholder="Address search..."
            onChange={handleInputChange}
            IconLeft={
              <div className="w-5 h-5 text-blue-grey-800">
                <SearchIcon />
              </div>
            }
          />
        </div>
        <Button data-testid="filter-comparables" variant="tertiary" onClick={handleShowFilter}>
          <Icon icon="icon-lt-filter-line" extraClasses="text-neutral-500 pointer-events-none" />
          {showFilter && <span className="text-sm font-medium">Close</span>}
          {!hasNoFilters && !showFilter && <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />}
        </Button>
      </div>
      {showFilter && (
        <div>
          <FiltersWithClear
            filters={state}
            setFilters={updateFilters}
            unitPreference={unitPreference}
            priceMode={RHPdisplayBy}
            dataSource={isZoopla(RHPdisplayBy) ? "hometrack" : "hmlr"}
          />
        </div>
      )}
      {!hasNoFilters && !showFilter && (
        <div className="p-2">
          <div className="p-3 h-12 rounded-xl border border-[#e3e7eb] justify-start items-center inline-flex w-full">
            <div className="flex items-start justify-start pr-2">
              <Icon icon="icon-lt-filter-line" extraClasses="text-amber-600 pointer-events-none" />
            </div>
            <span className="flex gap-4 text-base font-medium leading-normal">Filters are applied</span>
            <Button data-testid="reset-filters" variant="ghost" className="ml-auto" onClick={clearFilters}>
              {t("comparables.filters.clear")}
            </Button>
          </div>
        </div>
      )}
      <div>
        {filteredByAddress.length > 0 && (
          <TransactionsAccordion
            includedResults={filteredByAddress}
            loading={loading}
            setSelectedRecord={selectRecord}
          />
        )}
      </div>
    </>
  );
};

export function ComparablesSelectionSidebar(props: LayerTypeSelectionSidebarProps) {
  if (
    [SelectionType.LANDTECH_COMPARABLE_TRANSACTION, SelectionType.HOMETRACK_COMPARABLE_TRANSACTION].includes(
      props.selection.type
    )
  ) {
    return null;
  }

  return <ComparablesSelectionSidebarInner {...props} />;
}
